/* eslint-disable max-lines -- merges the legacy Vuex mutations.ts + actions.ts
   into the single Pinia store; splitting would break the shared ThisType-typed
   actions map (see idoStoreActions). */

// PRODUCT-26110 Pinia migration: single flat store replacing the root Vuex
// store — the legacy mutations and actions merge into one actions map (no
// name collisions; mutations already wrote state via `this`).
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import { defineStore } from 'pinia';

import load from '@evidentid/browser-framework/loader';
import { ChatParty } from '@evidentid/dashboard-commons/modules/online-chat/models';
import {
    createOnlineChatMessage,
} from '@evidentid/dashboard-commons/modules/online-chat/utils/createOnlineChatMessage';
import {
    CHAT_ERROR_RESPONSE_MSG,
} from '@evidentid/dashboard-commons/modules/online-chat/utils/session-states/constants';
import {
    addMessageToSessionHistory,
} from '@evidentid/dashboard-commons/modules/online-chat/utils/session-states/sessionHistory';
import {
    getAcademicProviders,
    getUtilityProviders,
    getDelegationLink,
    requestTransition,
    sendConsent,
    submitPayment,
    submitWebAttributes,
    setUp,
    tearDown,
    undoSetUp,
    verifyDocumentImages,
    SESSION_STORAGE_KEY,
    queryChatBot,
} from '@evidentid/ido-lib/idoWebClient';
import delay from '@evidentid/universal-framework/delay';
import redirect from '@evidentid/vue-commons/router/redirect';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import * as verificationConfig from '@/config/attributesVerification';
import { GlsAttributeType } from '@/config/gls';
import type Field from '@/fields/Field';
import glsApiClient from '@/glsApiClient';
import { isGlsSession } from '@/interfaces/SessionData';
import getCriminalOffensesList from '@/services/getCriminalOffensesList';
import getGlsSessionInterview from '@/services/getGlsSessionInterview';
import getSessionInterview from '@/services/getSessionInterview';
import { classifyLangTag } from '@/services/getWeglotLang';
import { createSessionLanguageStore } from '@/services/weglotLanguageStore';
import buildPreflightAttributesValues from '@/utils/buildPreflightAttributesValues';
import createMapWithKeys from '@/utils/createMapWithKeys';
import extractAttributeTypesFromValues from '@/utils/extractAttributeTypesFromValues';
import findQuestionsForAttributeTypes from '@/utils/findQuestionsForAttributeTypes';
import getVerificationFailureReason from '@/utils/getVerificationFailureReason';

import ErrorObject from './interfaces/ErrorObject';
import { type default as IdoState, Status, ValuesMap, VerificationResult, VerificationStatus } from './interfaces/IdoState';
function isCsrfTokenError(error: { reason?: string, message?: string } | null): boolean {
    return Boolean(
        error &&
        'message' in error &&
        `${error.message}`.includes('CSRF session'),
    );
}
function isCsrfTokenMismatchError(error: { reason?: string, message?: string } | null): boolean {
    return Boolean(
        error &&
        'message' in error &&
        `${error.message}`.includes('CSRF tokens do not match'),
    );
}
function isAuthError(error: { reason?: string, message?: string } | null): boolean {
    if (!error) {
        return false;
    }
    const reason = 'reason' in error ? error.reason : error.message;
    return `${reason}`.startsWith('auth/');
}
async function getGlsTransition(): Promise<{ transitionUri: string | null, status: string }> {
    const { interviewUrl } = await glsApiClient.getSession();
    return {
        status: interviewUrl ? 'transitionReady' : 'transitionNotNeeded',
        transitionUri: interviewUrl,
    };
}
function emailAlreadyExists(GlsAttributeType: Record<string, string>, values: ValuesMap, newEmail: string): boolean {
    const allEmails = flatten(Object.values(GlsAttributeType).map((key: string) => values[key]?.list || []));
    return allEmails.some((e) => e.toLowerCase() === newEmail.toLowerCase());
}
function errorContainsText(error: string | { error: string } | null): boolean {
    if (!error) {
        return false;
    }
    if (typeof error === 'string') {
        return Boolean(error.trim().length);
    }
    return Boolean(error.error && (error.error).trim().length);
}

import buildInitialState from './buildInitialState';
import RawIdoActions from './interfaces/RawIdoActions';
import RawIdoMutations from './interfaces/RawIdoMutations';

// Legacy Vuex mutation names — used by the action-test fixture to route
// `this.<mutation>()` calls to its commit spy (vs dispatch for actions).
export const legacyMutationNames: readonly string[] = [
    'setError',
    'setSnackbarContent',
    'setStatus',
    'setUserSession',
    'setInterviewDetails',
    'setGlsDetails',
    'setSubmissionStatus',
    'setValue',
    'completeQuestion',
    'setAcademicProviders',
    'setUtilityProviders',
    'setCriminalOffenses',
    'startDelegation',
    'finishDelegation',
    'failDelegation',
    'startVerification',
    'attemptVerificationResolution',
    'setVerificationStatus',
    'setStripeInstance',
    'finishPayment',
    'setConsentStatus',
    'setValuesOptionally',
    'startGlsResendLink',
    'finishGlsResendLink',
    'failGlsResendLink',
    'setLoadingAgentResponse',
] as const;

type IdoActionsMap = RawIdoMutations & RawIdoActions;

// Exported for the action-test fixture (fixtures/createActionContext.prereq.spec.ts),
// which replays these raw implementations against a mock context. ThisType gives the
// method bodies the same `this` (state + siblings) they get inside defineStore.
export const idoStoreActions: IdoActionsMap & ThisType<IdoState & IdoActionsMap> = {
    // --- legacy mutations ---

    setError(error: Parameters<RawIdoMutations['setError']>[0]) {
        this.error = error || null;
    },
    setSnackbarContent(configuration: Parameters<RawIdoMutations['setSnackbarContent']>[0]) {
        const permanent = Boolean(configuration?.permanent);
        this.snackbar = configuration
            ? { success: Boolean(configuration.success), message: configuration.message || null, permanent }
            : { success: false, message: null, permanent };
    },
    setStatus(status: Parameters<RawIdoMutations['setStatus']>[0]) {
        this.status = status;
    },
    setUserSession(session: Parameters<RawIdoMutations['setUserSession']>[0]) {
        this.session = session;

        if (session) {
            setUp(session);
        }
    },
    setInterviewDetails(payload: Parameters<RawIdoMutations['setInterviewDetails']>[0]) {
        // eslint-disable-next-line no-param-reassign
        payload = payload || {};

        this.user = payload.user || null;
        this.issuer = payload.issuer || null;
        this.questions = payload.questions || [];
        this.summary = payload.summary || null;
        this.requestStatus = payload.status || null;
        this.balance = payload.balance || null;
        this.featureFlags = payload.featureFlags || this.featureFlags;
        this.attributeSharingAgreements = payload.attributeSharingAgreements;

        // Initialize values
        const attributeTypes = this.questions.map((question) => question.attrType);
        this.values = { ...createMapWithKeys(attributeTypes, null), ...this.values };
        this.submission = { ...createMapWithKeys(attributeTypes, false), ...this.submission };
    },
    setGlsDetails(payload: Parameters<RawIdoMutations['setGlsDetails']>[0]) {
        this.gls = payload || null;
    },
    setSubmissionStatus(submissionMap: Parameters<RawIdoMutations['setSubmissionStatus']>[0]) {
        this.submission = {
            ...this.submission,
            ...submissionMap,
        };
    },
    setValue(payload: Parameters<RawIdoMutations['setValue']>[0]) {
        this.values = {
            ...this.values,
            [payload.name]: payload.value,
        };

        // Reset verification status after value change
        const verification = this.verificationsRequested[payload.name];
        if (verification?.result && verification.result !== VerificationResult.uninitialized) {
            this.verificationsRequested = {
                ...this.verificationsRequested,
                [payload.name]: {
                    ...verification,
                    result: VerificationResult.uninitialized,
                } as VerificationStatus,
            };
        }
    },
    completeQuestion(attrType: Parameters<RawIdoMutations['completeQuestion']>[0]) {
        // Mark selected question as completed
        this.questions = this.questions.map((question) => (
            question.attrType === attrType ? { ...question, complete: true } : question
        ));

        // When ID scan with Selfie required is finished, unlock the Selfie and/or Video Selfie question
        const currentQuestion = this.questions.find((question) => question.attrType === attrType);

        // Enable dependent attributes (i.e. for ID scan)
        const nextAttrTypes = currentQuestion?.metadata?.nextAttrTypes;
        if (nextAttrTypes) {
            this.questions = this.questions.map((question) => {
                if (nextAttrTypes.includes(question.attrType) && question.metadata?.hidden) {
                    return {
                        ...question,
                        metadata: { ...question.metadata, hidden: false },
                    };
                }
                return question;
            });
        }
    },
    setAcademicProviders(providers: Parameters<RawIdoMutations['setAcademicProviders']>[0]) {
        this.academicProviders = providers;
    },
    setUtilityProviders(providers: Parameters<RawIdoMutations['setUtilityProviders']>[0]) {
        this.utilityProviders = providers;
    },
    setCriminalOffenses(criminalOffenses: Parameters<RawIdoMutations['setCriminalOffenses']>[0]) {
        this.criminalOffenses = criminalOffenses;
    },
    startDelegation(attrType: Parameters<RawIdoMutations['startDelegation']>[0]) {
        const delegationStatus = this.delegationsRequested[attrType];
        const requests = delegationStatus ? delegationStatus.requests : 0;

        // The previous url is carried through a refetch on purpose: both forms request a link
        // from mounted(), so a remount would otherwise blank a working code back to the
        // placeholder for the length of the round trip.
        this.delegationsRequested = {
            ...this.delegationsRequested,
            [attrType]: {
                status: Status.loading,
                requests,
                url: delegationStatus?.url,
                expiresAt: delegationStatus?.expiresAt,
            },
        };
    },
    finishDelegation(payload: Parameters<RawIdoMutations['finishDelegation']>[0]) {
        const { attrType, url, expiresAt } = payload;
        const delegationStatus = this.delegationsRequested[attrType];
        const requests = delegationStatus ? delegationStatus.requests : 0;

        this.delegationsRequested = {
            ...this.delegationsRequested,
            [attrType]: { status: Status.success, requests: requests + 1, url, expiresAt },
        };
    },
    failDelegation(attrType: Parameters<RawIdoMutations['failDelegation']>[0]) {
        const delegationStatus = this.delegationsRequested[attrType];
        const requests = delegationStatus ? delegationStatus.requests : 0;

        // Keep the url: a failed refetch does not invalidate the link already on screen, and
        // showing "we couldn't build your QR code" over a working code is worse than either.
        this.delegationsRequested = {
            ...this.delegationsRequested,
            [attrType]: {
                status: Status.error,
                requests,
                url: delegationStatus?.url,
                expiresAt: delegationStatus?.expiresAt,
            },
        };
    },
    startVerification(payload: Parameters<RawIdoMutations['startVerification']>[0]) {
        const { attributeTypes } = payload;
        const defaults = {
            failures: 0,
        };

        for (const attrType of attributeTypes) {
            this.verificationsRequested = {
                ...this.verificationsRequested,
                [attrType]: {
                    ...defaults,
                    ...this.verificationsRequested[attrType],
                    result: VerificationResult.uploading,
                    resolutionAttempts: 0,
                    datumIds: null,
                    failureReason: null,
                },
            };
        }
    },
    attemptVerificationResolution(payload: Parameters<RawIdoMutations['attemptVerificationResolution']>[0]) {
        const { attributeTypes } = payload;
        const defaults = {
            failures: 0,
            result: VerificationResult.uninitialized,
        };

        for (const attrType of attributeTypes) {
            const previousStatus: any = this.verificationsRequested[attrType] || {};
            this.verificationsRequested = {
                ...this.verificationsRequested,
                [attrType]: {
                    ...defaults,
                    datumIds: null,
                    failureReason: null,
                    ...previousStatus,
                    resolutionAttempts: (previousStatus.resolutionAttempts || 0) + 1,
                },
            };
        }
    },
    setVerificationStatus(payload: Parameters<RawIdoMutations['setVerificationStatus']>[0]) {
        const { attributeTypes, datumIds, failureReason, result } = payload;
        const defaults = {
            resolutionAttempts: 0,
            failures: 0,
            result: VerificationResult.uninitialized,
            datumIds: null,
            failureReason: null,
        };

        for (const attrType of attributeTypes) {
            const previousStatus = this.verificationsRequested[attrType] || {};
            const nextStatus: VerificationStatus = {
                ...defaults,
                ...previousStatus,
            };

            if (datumIds) {
                nextStatus.datumIds = datumIds;
            }

            if (result === VerificationResult.failed) {
                nextStatus.result = VerificationResult.failed;
                nextStatus.failureReason = failureReason || null;
                nextStatus.failures += 1;
            } else if (result === VerificationResult.verified) {
                nextStatus.result = VerificationResult.verified;
            } else if (result) {
                nextStatus.result = result;
            }

            this.verificationsRequested = {
                ...this.verificationsRequested,
                [attrType]: nextStatus,
            };
        }
    },
    setStripeInstance(stripe: Parameters<RawIdoMutations['setStripeInstance']>[0]) {
        this.stripe = stripe;
    },
    finishPayment() {
        this.balance = null;
    },
    setConsentStatus(status: Parameters<RawIdoMutations['setConsentStatus']>[0]) {
        this.consentGiven = status;
    },
    setValuesOptionally(values: Parameters<RawIdoMutations['setValuesOptionally']>[0]) {
        const attributeTypes = Object.keys(values);
        const allowedAttributeTypes = attributeTypes.filter((attributeType) => {
            const question = this.questions.find((x) => x.attrType === attributeType);
            return (
                this.values[attributeType] == null || // Empty attributes
                    !question || // Static values, beside regular flow
                    question.complete // Already submitted
            );
        });

        if (allowedAttributeTypes.length > 0) {
            this.values = {
                ...this.values,
                ...pick(values, allowedAttributeTypes),
            };
        }
    },

    startGlsResendLink(emails: Parameters<RawIdoMutations['startGlsResendLink']>[0]) {
        this.glsResendLinkStatus = {
            ...this.glsResendLinkStatus,
            ...mapValues(keyBy(emails, (email) => email), () => Status.loading),
        };
    },

    finishGlsResendLink(emails: Parameters<RawIdoMutations['finishGlsResendLink']>[0]) {
        this.glsResendLinkStatus = {
            ...this.glsResendLinkStatus,
            ...mapValues(keyBy(emails, (email) => email), () => Status.success),
        };
    },

    failGlsResendLink(emails: Parameters<RawIdoMutations['failGlsResendLink']>[0]) {
        this.glsResendLinkStatus = {
            ...this.glsResendLinkStatus,
            ...mapValues(keyBy(emails, (email) => email), () => Status.error),
        };
    },
    setLoadingAgentResponse(status: OperationStatus) {
        this.onlineChat = {
            ...this.onlineChat,
            loadAgentResponse: status,
        };
    },

    // --- legacy actions ---

    async showError(error: Parameters<RawIdoActions['showError']>[0]) {
        const finalError: ErrorObject | { message: string } | null
            = typeof error === 'string' ? { reason: error } : error;
        const reason = (finalError && 'reason' in finalError && finalError.reason) || null;

        if (isCsrfTokenMismatchError(finalError)) {
            const getCookieByName = (name: string): string | null => {
                const match = document.cookie.split(`${name}=`);
                if (match.length === 2) {
                    return match.pop()?.split(';').shift() || null;
                }
                return null;
            };
            const idoWebUrl = getCookieByName('shortUrl');
            if (idoWebUrl) {
                window.location.href = idoWebUrl;
            }
            return;
        }
        // Handle missing CSRF token as 'unauthorized-ido' error
        if (isCsrfTokenError(finalError)) {
            this.setError({ reason: 'unauthorized-ido' });
            return;
        }

        // Handle all authentication problems as 'unauthorized-id' error
        if (isAuthError(finalError)) {
            this.setError({ reason: 'unauthorized-ido' });
            return;
        }

        // Handle authorization errors in GLS flow
        const session = this.session;
        if (session && isGlsSession(session)) {
            const isUnauthorized = reason === 'gls/unauthorized';
            const isSessionExpired = reason === 'gls/session-expired';

            if (isSessionExpired) {
                await this.displaySnackbar({
                    success: false,
                    message: 'Your session has expired, please log in again.',
                });
                await this.logOut({ localOnly: true });
            }

            if (isSessionExpired || isUnauthorized) {
                await this.redirectLocally({
                    name: 'google-business-verification-v2',
                    params: { caseId: `${session.caseId}` },
                });
                return;
            }
        }

        // Other errors should be passed down
        this.setError(finalError || null);
    },

    displaySnackbar(configuration: Parameters<RawIdoActions['displaySnackbar']>[0]) {
        this.setSnackbarContent(
            configuration
                ? { success: false, permanent: false, message: null, ...configuration }
                : null,
        );
    },

    setSessionData(sessionData: Parameters<RawIdoActions['setSessionData']>[0]) {
        this.setUserSession(sessionData);
    },

    async loadInterview(sessionData: Parameters<RawIdoActions['loadInterview']>[0]) {
        this.setStatus(Status.loading);

        // Handle empty session data
        if (!sessionData) {
            await this.showError('unknown-request');
            this.setStatus(Status.error);
            this.setInterviewDetails(null);
            return;
        }

        // Initialize data loading
        this.setUserSession(sessionData);

        // Load and set-up all data
        try {
            const rawSessionInterview = isGlsSession(sessionData)
                ? await getGlsSessionInterview(sessionData)
                : await getSessionInterview(sessionData);
            const sessionInterview = omit(rawSessionInterview, [ 'gls' ]);
            const hasPaymentRequired = parseFloat(`${sessionInterview.balance?.amount}`) > 0;
            const attributeTypes = sessionInterview.questions
                .filter((question) => !question.complete)
                .map((question) => question.attrType);

            // Set-up GLS details
            this.setGlsDetails(rawSessionInterview.gls || null);
            // Set-up interview data
            this.setInterviewDetails(sessionInterview);
            this.setStatus(Status.success);

            // Set-up initial values
            if (sessionInterview.values && Object.keys(sessionInterview.values).length > 0) {
                this.setValuesOptionally(sessionInterview.values);
            }

            // Prefetch (asynchronously) additional attributes data for not completed questions
            if (attributeTypes.length > 0) {
                this.fetchAttributeData(attributeTypes);
            }

            // Initialize Stripe payment provider
            if (hasPaymentRequired) {
                await this.initializeStripe();
            }
        } catch (error) {
            console.log(error, 'actions/loadInterview');
            this.setInterviewDetails(null);
            this.setStatus(Status.error);
            if (isCsrfTokenError(error)) {
                // Show more friendly error when the request is invalid
                await this.showError('unknown-request');
            } else {
                await this.showError(error || 'unexpected');
            }
        }
    },

    async reloadInterview({ blockImmediateFinish }: Parameters<RawIdoActions['reloadInterview']>[0]) {
        // Extract data from current app state
        const {
            session, issuer, user, questions, balance,
            summary, requestStatus, featureFlags, gls,
            attributeSharingAgreements,
        } = this;
        const existingAttributeTypes = questions.map((question) => question.attrType);
        const isSubmitting = Object.values(this.submission).includes(true);
        const hadPaymentRequired = parseFloat(`${balance?.amount}`) > 0;

        // Ignore when there are no session data available, or submission is in progress
        if (!session || isSubmitting) {
            return;
        }

        try {
            // Load interview details
            const rawSessionInterview = isGlsSession(session)
                ? await getGlsSessionInterview(session, blockImmediateFinish)
                : await getSessionInterview(session, user, issuer, blockImmediateFinish);
            const sessionInterview = omit(rawSessionInterview, [ 'gls' ]);

            // Do not update interview when there were local changes meanwhile
            const isSubmittingAfter = Object.values(this.submission).includes(true);
            const hasQuestionsChanges = this.questions !== questions;
            if (isSubmittingAfter || hasQuestionsChanges) {
                return;
            }

            // Find differences
            const hasPaymentRequired = parseFloat(`${sessionInterview.balance?.amount}`) > 0;
            const newAttributeTypes = sessionInterview.questions
                .filter((question) => !question.complete)
                .map((question) => question.attrType)
                .filter((attrType) => !existingAttributeTypes.includes(attrType));

            // Update GLS details if expected
            if (!isEqual(gls, rawSessionInterview.gls || null)) {
                this.setGlsDetails(rawSessionInterview.gls || null);
            }

            // Ignore results, if the previous details are exactly the same. It's mostly to clean up our logs.
            const prevSessionInterview = {
                status: requestStatus,
                user,
                issuer,
                questions,
                summary,
                balance,
                featureFlags,
                ...(attributeSharingAgreements !== undefined && { attributeSharingAgreements }),
            };

            if (isEqual(omit(sessionInterview, [ 'values' ]), prevSessionInterview)) {
                // Update values for completed questions if these has changed
                const completedAttrTypes = questions.filter((question) => question.complete).map((x) => x.attrType);
                const prevValues = pick(this.values, completedAttrTypes);
                const nextValues = pick(sessionInterview.values || {}, completedAttrTypes);
                if (!isEqual(prevValues, nextValues)) {
                    if (nextValues && Object.keys(nextValues).length > 0) {
                        this.setValuesOptionally(nextValues);
                    }
                }
                return;
            }

            // Save new interview data
            this.setInterviewDetails(sessionInterview);
            this.setStatus(Status.success);

            // Set-up initial/completed values
            if (sessionInterview.values && Object.keys(sessionInterview.values).length > 0) {
                this.setValuesOptionally(sessionInterview.values);
            }

            // Prefetch (asynchronously) additional attributes data for not completed questions
            if (newAttributeTypes.length > 0) {
                this.fetchAttributeData(newAttributeTypes);
            }

            // Initialize Stripe payment provided, when it's newly required
            if (!hadPaymentRequired && hasPaymentRequired) {
                await this.initializeStripe();
            }
        } catch (error) {
            // Only warn about this error,
            // most likely we don't care about errors
            console.warn('Session interview reload failed', error);
        }
    },

    async logOut({ localOnly }: Parameters<RawIdoActions['logOut']>[0]) {
        const { session, questions, gls } = this;

        try {
            if (localOnly) {
                await undoSetUp();
            } else if (session && isGlsSession(session)) {
                await Promise.all([
                    tearDown(),
                    glsApiClient.clearSession(),
                ]);
            } else {
                await tearDown();
            }

            // Race condition: don't clear interview when it has already changed
            if (isEqual(this.gls, gls) && isEqual(this.questions, questions)) {
                this.setInterviewDetails(null);
                this.setStatus(Status.loggedOut);
            }

            // Race condition: don't clear the session object when it has already changed
            if (this.session === session) {
                this.setUserSession(null);
            }
        } catch (error) {
            await this.showError(error);
        }
    },

    changeValue(payload: Parameters<RawIdoActions['changeValue']>[0]) {
        this.setValue(payload);
    },

    async submitAttributes({ attributes, onUploadProgress }: Parameters<RawIdoActions['submitAttributes']>[0]) {
        // Get list of attribute types changed
        const attributeTypes = extractAttributeTypesFromValues(attributes, this.questions);

        // Retrieve questions which are sent
        const questions = findQuestionsForAttributeTypes(this.questions, attributeTypes);

        // Throw error, when trying to update not existing question only
        if (questions.length === 0) {
            await this.showError('unexpected');
            return;
        }

        // Start fields submission
        this.setSubmissionStatus(createMapWithKeys(attributeTypes, true));

        try {
            // Submit to back-end
            const request = submitWebAttributes(attributes);

            // Track upload progress with precision up to 0.001 (0.1%)
            if (onUploadProgress) {
                request.onUploadProgressChange(onUploadProgress);
            }

            // Wait for back-end response
            await request;

            // Mark questions as completed
            for (const question of questions) {
                this.completeQuestion(question.attrType);
            }
        } catch (error) {
            await this.showError('submission-failed');
        }

        // Mark all questions as not in submission process
        this.setSubmissionStatus(createMapWithKeys(attributeTypes, false));
    },

    async verifyAttributes({ attributes, onUploadProgress }: Parameters<RawIdoActions['verifyAttributes']>[0]) {
        // Get list of attribute types changed
        const attributeTypes = extractAttributeTypesFromValues(attributes, this.questions);

        // Retrieve questions which are sent
        const questions = findQuestionsForAttributeTypes(this.questions, attributeTypes);

        // Throw error, when trying to update not existing question only
        if (questions.length === 0) {
            await this.showError('unexpected');
            return;
        }

        // Build preflight attributes
        const preflightAttributesValues = buildPreflightAttributesValues(attributes);

        // Start fields submission
        this.setSubmissionStatus(createMapWithKeys(attributeTypes, true));
        this.startVerification({ attributeTypes });

        try {
            // Submit to back-end
            const request = submitWebAttributes(preflightAttributesValues);

            // Track upload progress with precision up to 0.001 (0.1%)
            if (onUploadProgress) {
                request.onUploadProgressChange(onUploadProgress);
            }

            // Wait for back-end response
            const { datumIds } = await request;

            // Mark as uploaded
            this.setVerificationStatus({
                attributeTypes,
                result: VerificationResult.verifying,
                datumIds,
            });

            // Ask AP first time so it will acknowledge these images
            let response = await verifyDocumentImages(datumIds);
            let resolutionAttempts = 0;

            // Try `maxPollingTimes` or less, until we will receive information about
            while (response.status === 'WAITING' && resolutionAttempts < verificationConfig.maxPollingTimes) {
                // Wait selected time
                await delay(verificationConfig.pollingDelayTimeMs);

                // Ask back-end for verification status
                response = await verifyDocumentImages(datumIds);

                // Register another verification attempt
                resolutionAttempts++;
                this.attemptVerificationResolution({ attributeTypes });
            }

            // Handle response data
            switch (response.status) {
                case 'WAITING':
                    this.setVerificationStatus({
                        attributeTypes,
                        result: VerificationResult.timeout,
                    });
                    break;
                case 'FAILED':
                    this.setVerificationStatus({
                        attributeTypes,
                        result: VerificationResult.failed,
                        failureReason: getVerificationFailureReason(response.reasons),
                    });
                    break;
                case 'OK':
                    this.setVerificationStatus({
                        attributeTypes,
                        result: VerificationResult.verified,
                    });

                    for (const question of questions) {
                        this.completeQuestion(question.attrType);
                    }
                    break;
                default:
                    throw new Error('Invalid verification response format');
            }
        } catch (error) {
            this.setVerificationStatus({
                attributeTypes,
                result: VerificationResult.error,
            });
        }

        // Mark submission as finished
        this.setSubmissionStatus(createMapWithKeys(attributeTypes, false));
    },

    async fetchAttributeData(attributeTypes: Parameters<RawIdoActions['fetchAttributeData']>[0]) {
        try {
            const questions = findQuestionsForAttributeTypes(this.questions, attributeTypes);
            const types = uniq(questions.map((question) => question.type));
            // Lazy import: fields' components transitively read this store —
            // a static import here would be a runtime circular dependency.
            const { default: getField } = await import('@/fields/getField');
            const fields = types.map(getField).filter(Boolean) as Field<any>[];

            await Promise.all(fields.map((field) =>
                field.ready || field.initialize(this as unknown as import('./interfaces/IdoStore').IdoStore)));
        } catch (error) {
            await this.showError(error);
        }
    },

    async loadAcademicProviders() {
        try {
            this.setAcademicProviders(await getAcademicProviders());
        } catch (error) {
            await this.showError(error);
        }
    },

    async loadUtilityProviders() {
        try {
            this.setUtilityProviders(await getUtilityProviders());
        } catch (error) {
            await this.showError(error);
        }
    },

    async loadCriminalOffenses() {
        try {
            this.setCriminalOffenses(await getCriminalOffensesList());
        } catch (error) {
            await this.showError(error);
        }
    },

    async delegate(attrType: Parameters<RawIdoActions['delegate']>[0]) {
        const userSession = this.session;

        // Validate user session
        if (!userSession) {
            await this.displaySnackbar({
                message: 'We couldn\'t build your QR code. Please refresh the page.',
                success: false,
            });
            return;
        }

        // GLS sessions shouldn't request delegation, as it is not supported
        if (isGlsSession(userSession)) {
            await this.showError('unexpected');
            return;
        }

        // Fetch the hand-off link. There is no send limit: the link is deterministic
        // for a given session, so asking again returns the same URL and just refreshes
        // how long it stays resolvable.
        this.startDelegation(attrType);
        try {
            const hash = `#!/interview/${userSession.rprId}`;
            const path = location.pathname.replace(/^\/v\//, '/');
            // The phone is a different browser with its own empty session storage, so the
            // language has to travel inside the link or the hand-off arrives in English.
            // idoweb keeps the query it is given and merely adds its canary to it, so a tag
            // placed here survives the round trip through the short URL and the auth redirect.
            const remembered = classifyLangTag(createSessionLanguageStore().read());
            const langQuery = remembered.kind === 'language' ? `&lang=${remembered.tag}` : '';
            const { url, expiresAt } = await getDelegationLink({
                continueUrl: `${location.origin}${path}?q=${attrType}${langQuery}${hash}`,
                enableReturn: false,
            });
            this.finishDelegation({ attrType, url, expiresAt });
        } catch (error) {
            this.failDelegation(attrType);
            this.displaySnackbar({
                message: 'We couldn\'t build your QR code. Try again in a few minutes, or open the link in the verification email directly on a mobile device. If any issues persist, contact support@evidentid.com',
                success: false,
                permanent: true,
            });
        }
    },

    async initializeStripe() {
        if (!this.stripe) {
            await load('https://js.stripe.com/v3/');
            // eslint-disable-next-line
                // @ts-ignore: getting from global window
            this.setStripeInstance(window.Stripe(deploy.WEB_PUBLIC_STRIPE_PUBLIC_KEY));
        }
    },

    async pay(stripeTokenId: Parameters<RawIdoActions['pay']>[0]) {
        try {
            await submitPayment({ id: stripeTokenId });

            this.finishPayment();
        } catch (error) {
            await this.showError(error);
        }
    },

    async sendConsent() {
        try {
            const session = this.session;

            // Handle case when user is not logged in and somehow sends consent
            if (!session) {
                await this.showError('unauthorized-ido');
                return;
            }

            // Send consent for sharing data, when it is not GLS session (which doesn't support it)
            if (!isGlsSession(session)) {
                await sendConsent(session.rprId);
            }

            // Otherwise, mark that it has succeed
            this.setConsentStatus(true);
        } catch (error) {
            await this.showError('submission-failed');
        }
    },

    async requestTransition() {
        try {
            const session = this.session;

            // Handle case when user is not logged in and somehow requests transition
            if (!session) {
                await this.showError('unauthorized-ido');
                return;
            }

            // Get information about required transition
            const { status, transitionUri } = isGlsSession(session)
                ? await getGlsTransition()
                : await requestTransition(session.rprId);

            // Decide what to do with current interview
            if (status === 'transitionNotNeeded') {
                this.setStatus(Status.finished);
            } else if (status === 'transitionReady') {
                this.setStatus(Status.transition);
                redirect(transitionUri);
            }
        } catch (error) {
            await this.showError(error);
        }
    },

    async submitGlsData(data: Parameters<RawIdoActions['submitGlsData']>[0]) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        // Inform about submission of all attributes
        const attrTypes = this.questions.map((x) => x.attrType);
        this.setSubmissionStatus(createMapWithKeys(attrTypes, true));

        try {
            // Request submission
            await glsApiClient.submit(session.caseId, data);

            // Reload interview, to update data, session and completion status, based on back-end
            for (const question of this.questions) {
                this.completeQuestion(question.attrType);
            }
        } catch (error) {
            // Show error
            if (error?.status === 'invalid_business_address') {
                await this.displaySnackbar({
                    success: false,
                    permanent: true,
                    message: 'The address you entered does not match a known address. Please enter the business address as it appears on Google Maps.',
                });
            } else {
                await this.showError(error);
            }
        } finally {
            // Mark all attributes as submitted
            this.setSubmissionStatus(createMapWithKeys(attrTypes, false));
        }
    },

    async deleteGlsFieldWorker(email: Parameters<RawIdoActions['deleteGlsFieldWorker']>[0]) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        const attributeType = GlsAttributeType.fieldWorkers in this.values
            ? GlsAttributeType.fieldWorkers
            : GlsAttributeType.businessOwnersAndFieldworkers;

        // Build next list of field workers
        const { list } = this.values[attributeType] || { list: [] };
        const nextList = list.filter((x: string) => x !== email);

        // Ensure that there was any change
        if (list.length === nextList.length) {
            return;
        }

        // Inform about field workers update in progress
        this.setSubmissionStatus({ [attributeType]: true });

        try {
            // Request fieldworker deletion
            await glsApiClient.deleteFieldWorker(session.caseId, email);

            // Update current value displayed
            await this.changeValue({
                name: attributeType,
                value: { confirmed: true, list: nextList },
            });
        } catch (error) {
            await this.showError(error);
        } finally {
            // Inform about end of submission
            this.setSubmissionStatus({ [attributeType]: false });
        }
    },

    async addGlsFieldWorker(email: string) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        if (emailAlreadyExists(GlsAttributeType, this.values, email)) {
            await this.displaySnackbar({
                success: false,
                message: 'This e-mail address is already used.',
            });
            return;
        }

        const attributeType = GlsAttributeType.fieldWorkers in this.values
            ? GlsAttributeType.fieldWorkers
            : GlsAttributeType.businessOwnersAndFieldworkers;

        // Build next list of field workers
        const { list } = this.values[attributeType] || { list: [] };

        // Inform about field workers update in progress
        this.setSubmissionStatus({ [attributeType]: true });

        try {
            // Request fieldworker add
            await glsApiClient.addFieldWorker(session.caseId, email);

            // Update current value displayed
            await this.changeValue({
                name: attributeType,
                value: { confirmed: true, list: [ ...list, email ] },
            });
        } catch (error) {
            if (errorContainsText(error)) {
                await this.displaySnackbar({
                    success: false,
                    message: error.error || error,
                });
            } else {
                await this.showError(error);
            }
        } finally {
            // Inform about end of submission
            this.setSubmissionStatus({ [attributeType]: false });
        }
    },

    async addGlsBusinessOwner(email: string) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        if (emailAlreadyExists(GlsAttributeType, this.values, email)) {
            await this.displaySnackbar({
                success: false,
                message: 'This e-mail address is already used.',
            });
            return;
        }

        const attributeType = GlsAttributeType.businessOwners in this.values
            ? GlsAttributeType.businessOwners
            : GlsAttributeType.businessOwnersAndFieldworkers;

        // Build next list of business owners
        const { list } = this.values[attributeType] || { list: [] };

        // Inform about field workers update in progress
        this.setSubmissionStatus({ [attributeType]: true });

        try {
            // Request business owner add
            await glsApiClient.addBusinessOwner(session.caseId, email);

            // Update current value displayed
            await this.changeValue({
                name: attributeType,
                value: { confirmed: true, list: [ ...list, email ] },
            });
        } catch (error) {
            if (errorContainsText(error)) {
                await this.displaySnackbar({
                    success: false,
                    message: error.error || error,
                });
            } else {
                await this.showError(error);
            }
        } finally {
            // Inform about end of submission
            this.setSubmissionStatus({ [attributeType]: false });
        }
    },

    async resendGlsSubmissionLink(emails: Parameters<RawIdoActions['resendGlsSubmissionLink']>[0]) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        // Split e-mails
        const existingBusinessOwners = this.values[GlsAttributeType.businessOwners]?.list || [];
        const [ businessOwners, fieldWorkers ] = partition(emails, (email) => existingBusinessOwners.includes(email));

        // Inform about submission in progress
        this.startGlsResendLink(emails);

        try {
            // Call API
            const [
                { failed: failedBusinessOwners },
                { failed: failedFieldWorkers },
            ] = await Promise.all([
                businessOwners.length > 0
                    ? glsApiClient.resendBusinessOwnersLink(session.caseId, businessOwners)
                    : Promise.resolve({ failed: [] as string[] }),
                fieldWorkers.length > 0
                    ? glsApiClient.resendFieldWorkersLink(session.caseId, fieldWorkers)
                    : Promise.resolve({ failed: [] as string[] }),
            ]);

            // Update submission status
            const failed = failedBusinessOwners.concat(failedFieldWorkers);
            const succeed = emails.filter((email) => !failed.includes(email));
            if (failed.length > 0) {
                this.failGlsResendLink(failed);
            }
            if (succeed.length > 0) {
                this.finishGlsResendLink(succeed);
            }
        } catch (error) {
            // Mark as failed and throw error (when unknown)
            this.failGlsResendLink(emails);
            await this.showError(error);
        }
    },

    async deleteGlsBusinessOwner(email: Parameters<RawIdoActions['deleteGlsBusinessOwner']>[0]) {
        const session = this.session;

        // Handle situation when there is regular Evident session used
        if (!session || !isGlsSession(session)) {
            await this.showError('unexpected');
            return;
        }

        // Revoke primary business owner
        if (email === this.gls?.primaryBusinessOwner) {
            await this.displaySnackbar({
                success: false,
                message: 'You can\'t delete primary business owner.',
            });
            return;
        }

        const attributeType = GlsAttributeType.businessOwners in this.values
            ? GlsAttributeType.businessOwners
            : GlsAttributeType.businessOwnersAndFieldworkers;

        // Build next list of field workers
        const { list } = this.values[attributeType] || { list: [] };
        const nextList = list.filter((x: string) => x !== email);

        // Ensure that there was any change
        if (list.length === nextList.length) {
            return;
        }

        // Inform about field workers update in progress
        this.setSubmissionStatus({ [attributeType]: true });

        try {
            // Request fieldworker deletion
            await glsApiClient.deleteBusinessOwner(session.caseId, email);

            // Update current value displayed
            await this.changeValue({
                name: attributeType,
                value: { confirmed: true, list: nextList },
            });
        } catch (error) {
            await this.showError(error);
        } finally {
            // Inform about end of submission
            this.setSubmissionStatus({ [attributeType]: false });
        }
    },

    async redirectLocally(location: Parameters<RawIdoActions['redirectLocally']>[0]) {
        // FIXME: Change usage of actions object, to be created each time, and get a router as argument for factory
        // The import of idoApplication is circular (and would drag the whole application into
        // any spec that touches the store module), so it stays deferred. A dynamic import()
        // is as lazy as the require() it replaces, and unlike require() it works under
        // Vite, which does not accept CommonJS in bundled source.
        const app = (await import('@/idoApplication')).default;
        if (app.router) {
            await app.router.push(location);
        }
    },
    async askAgent(payload: { question: string, sessionId: string }) {
        this.setLoadingAgentResponse(OperationStatus.loading);
        try {
            const response = await queryChatBot(payload.question, payload.sessionId);
            const agentMessage = createOnlineChatMessage(response.answer.content, ChatParty.agent);
            addMessageToSessionHistory(agentMessage);
            this.setLoadingAgentResponse(OperationStatus.success);
        } catch (error) {
            console.error(error);
            addMessageToSessionHistory(createOnlineChatMessage(CHAT_ERROR_RESPONSE_MSG, ChatParty.agent));
            this.setLoadingAgentResponse(OperationStatus.error);
        }
    },
};

export const useIdoStore = defineStore('ido', {
    state: buildInitialState,
    actions: idoStoreActions,
});
